// react
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';

// material ui
import { Box, Button, Stack, Typography } from '@mui/material';

// api
import type { GetDeviceDto } from '@/api/generated/recalc/endpoints/v2/devices';
import { usePutDeviceMutation } from '@/api/generated/recalc/endpoints/v2/devices';

// shared components
import DanishAddressRegisterAutocomplete from '@/components/datafordeler/DanishAddressRegisterAutocomplete';
import type { DanishAddressRegisterSelection } from '@/components/datafordeler/DanishAddressRegisterAutocomplete';
import useDarReverseGeocode from '@/components/datafordeler/useDarReverseGeocode';
import LocationMap from '@/components/maps/LocationMap';
import { SubmitButton } from '@/components/ui/button';
import InsetDrawer from '@/components/ui/drawer/InsetDrawer';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

interface DeviceLocationDrawerProps {
  open: boolean;
  device?: GetDeviceDto | null;
  onClose: () => void;
}

type DeviceLocationFormProps = {
  device?: GetDeviceDto | null;
  onClose: () => void;
};

function getInitialLocationState(device?: GetDeviceDto | null) {
  return {
    address: device?.location?.address?.trim() ?? '',
    latitude: device?.location?.latitude ?? null,
    longitude: device?.location?.longitude ?? null,
    accuracy: device?.location?.accuracyInMeters ?? null,
  };
}

const DeviceLocationDrawerForm = ({
  device,
  onClose,
}: DeviceLocationFormProps) => {
  const { t } = useTranslation('units-gateways-sensors');
  const dispatch = useDispatch();
  const [putDevice, { isLoading: isSaving }] = usePutDeviceMutation();

  const initialLocation = getInitialLocationState(device);

  const [locationAddress, setLocationAddress] = useState(
    initialLocation.address,
  );
  const [locationLatitude, setLocationLatitude] = useState<number | null>(
    initialLocation.latitude,
  );
  const [locationLongitude, setLocationLongitude] = useState<number | null>(
    initialLocation.longitude,
  );
  const [locationAccuracy, setLocationAccuracy] = useState<number | null>(
    initialLocation.accuracy,
  );

  const reverseGeocodedLabel = useDarReverseGeocode(
    locationAddress === '' ? locationLatitude : null,
    locationAddress === '' ? locationLongitude : null,
  );

  const displayLocationLabel = locationAddress || reverseGeocodedLabel || '';
  const hasCoords = locationLatitude != null && locationLongitude != null;
  const hasLocationValues = useMemo(
    () =>
      Boolean(locationAddress.trim() || hasCoords || locationAccuracy != null),
    [hasCoords, locationAccuracy, locationAddress],
  );

  const handleLocationSelect = (
    selection: DanishAddressRegisterSelection | null,
  ) => {
    const latitude = Number(selection?.details?.coordinates?.lat);
    const longitude = Number(selection?.details?.coordinates?.lon);

    if (!Number.isNaN(latitude) && !Number.isNaN(longitude)) {
      setLocationLatitude(latitude);
      setLocationLongitude(longitude);
      setLocationAddress(selection?.parsed?.tekst || '');
      setLocationAccuracy(null);
    }
  };

  const handleClearLocation = () => {
    setLocationAddress('');
    setLocationLatitude(null);
    setLocationLongitude(null);
    setLocationAccuracy(null);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!device?.deviceId) {
      return;
    }

    if ((locationLatitude == null) !== (locationLongitude == null)) {
      dispatch(
        showSnackbar({
          message: t('devices.locationValidationRequired'),
          severity: 'error',
        }),
      );
      return;
    }

    const existingLocation = device.location;
    const existingTags =
      device.tags
        ?.map((tag) => ({
          key: tag?.key?.trim() ?? '',
          value: tag?.value?.trim() ?? '',
        }))
        .filter((tag) => tag.key && tag.value) ?? [];
    const location = hasLocationValues
      ? {
          latitude: locationLatitude,
          longitude: locationLongitude,
          accuracyInMeters: locationAccuracy,
          address: locationAddress.trim() || null,
          addressAutoLookup: existingLocation?.addressAutoLookup ?? false,
        }
      : undefined;

    const result = await putDevice({
      deviceId: device.deviceId,
      putDeviceDto: {
        displayName: device.displayName?.trim() || null,
        description: device.description?.trim() || null,
        tags: existingTags.length > 0 ? existingTags : [],
        ...(location ? { location } : { location: {} }),
      },
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: t('devices.updatedSuccess', {
            name: device.displayName?.trim() || t('devices.detailTitle'),
          }),
          severity: 'success',
        }),
      );

      onClose();
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 1, py: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          {t('devices.locationSectionDescription')}
        </Typography>
      </Box>

      <Box
        id="device-location-form"
        component="form"
        onSubmit={handleSubmit}
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1, py: 1.5 }}
      >
        <Stack spacing={3} mb={20}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography variant="overline" color="text.secondary">
                {t('common.editLocation')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('devices.locationSectionDescription')}
              </Typography>
            </Box>

            <Button
              variant="outlined"
              onClick={handleClearLocation}
              disabled={!hasLocationValues || isSaving}
            >
              {t('devices.clearLocation')}
            </Button>
          </Stack>

          <Stack spacing={2}>
            {hasCoords ? (
              <LocationMap
                zoom={15}
                pins={[
                  {
                    latitude: locationLatitude,
                    longitude: locationLongitude,
                    label: device?.displayName?.trim() || '',
                  },
                ]}
              />
            ) : (
              <Box
                sx={{
                  height: 400,
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  color: 'text.disabled',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <RoomOutlinedIcon sx={{ fontSize: 40 }} />
                <Typography variant="body2">
                  {t('gateways.noLocationSet')}
                </Typography>
                <Typography variant="caption">
                  {t('gateways.searchAddressHint')}
                </Typography>
              </Box>
            )}

            <DanishAddressRegisterAutocomplete
              value={displayLocationLabel}
              onAddressSelect={handleLocationSelect}
              autocompleteProps={{ disabled: isSaving, fullWidth: true }}
              textFieldProps={{ required: false, size: 'small' }}
            />
          </Stack>
        </Stack>
      </Box>

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={onClose} disabled={isSaving}>
          {t('common.cancel')}
        </Button>
        <SubmitButton form="device-location-form" disabled={isSaving}>
          {t('common.save')}
        </SubmitButton>
      </Stack>
    </Box>
  );
};

const DeviceLocationDrawer = ({
  open,
  device,
  onClose,
}: DeviceLocationDrawerProps) => {
  const { t } = useTranslation('units-gateways-sensors');

  const formResetKey = [
    device?.deviceId ?? 'unknown-device',
    device?.location?.address?.trim() ?? '',
    device?.location?.latitude ?? '',
    device?.location?.longitude ?? '',
    device?.location?.accuracyInMeters ?? '',
    open ? 'open' : 'closed',
  ].join(':');

  return (
    <InsetDrawer
      anchor="right"
      open={open}
      onClose={onClose}
      title={t('common.editLocation')}
      width={760}
    >
      {open ? (
        <DeviceLocationDrawerForm
          key={formResetKey}
          device={device}
          onClose={onClose}
        />
      ) : null}
    </InsetDrawer>
  );
};

export default DeviceLocationDrawer;
